import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'reactstrap';
import classNames from 'classnames';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';

import { toggleAppointmentRequestsModalClass } from '../../ParentProfile/utils/toggleAppointmentRequestsModalClass';
import TableFilterComponent from '../../../../shared/components/form/TableFilterComponent';
import TableHeaderLabel from '../../../../shared/components/table/TableHeaderLabel';
import CommCopyHistoryBlockedPrompt from '../../../../shared/components/table/CommCopyHistoryBlockedPrompt';
import { getStaffDoctorsByFacility } from '../../../../redux/actions/staffActions';
import { handleRequest } from '../../../../redux/services/APIService';
import { SortingField } from '../../../common/SortingCell';
import {
  getSortFieldName,
  getDefaultSortOrder,
} from '../../Common/SortBy/utils';
import { APPOINTMENT_REQUEST_MESSAGE_STATUS } from '../../../../config/app.config';
import { buildMessageHistoryTranscript } from '../../../../utils/buildMessageHistoryTranscript';

import {
  tab,
  formatPreferredDateRange,
  useAppointmentRequestModalContext,
  parseAvailabilityInfo,
} from '../utils';

import { ReactComponent as CalendarMonthIcon } from '../../../../shared/img/calendar_month.svg';

const COPIED_BADGE_DURATION_MS = 1500;

const getFilterField = (key) => {
  return key === 'doctorFullName' ? 'doctorId' : key;
};

export function AppointmentRequestsTable({
  heads,
  rows,
  handleFilter,
  handleSort,
  filters,
  sortField,
  sortOrder,
  stickyTop,
  emptyStateMessage = 'No Appointment Requests to display',
}) {
  const dispatch = useDispatch();
  const [copyingRequestId, setCopyingRequestId] = useState(null);
  const [copiedRequestId, setCopiedRequestId] = useState(null);
  const [copyBlockedRequestId, setCopyBlockedRequestId] = useState(null);
  const copyBadgeTimeoutRef = useRef(null);

  const doctors = useSelector(
    (state) => state.staff.staffDoctorsByFacility || [],
  );
  const facilityId = useSelector((state) => state.user?.profile?.facility?._id);
  const { petsAppointmentRequestsNumber, isPetAppointmentRequestsModalOpen } =
    useSelector((state) => state.pets);

  const {
    openAppointmentRequestModal,
    handleSetAppointmentRequestData,
    handleSetDoctors,
    setIsMessageModalOpen,
  } = useAppointmentRequestModalContext();

  useEffect(() => {
    if (facilityId) {
      dispatch(getStaffDoctorsByFacility());
    }
  }, [dispatch, facilityId]);

useEffect(() => {
  return () => {
    if (copyBadgeTimeoutRef.current) {
      clearTimeout(copyBadgeTimeoutRef.current);
    }
  };
}, []);

useEffect(() => {
  if (!copyBlockedRequestId) {
    return undefined;
  }

  const dismissBlockedPrompt = () => {
    setCopyBlockedRequestId(null);
  };

  document.addEventListener('mousedown', dismissBlockedPrompt);
  return () => {
    document.removeEventListener('mousedown', dismissBlockedPrompt);
  };
}, [copyBlockedRequestId]);

const handleStatusFilter = (key, list) => {
    const filterField = getFilterField(key);

    handleFilter(filterField, list);
  };

  const handleOpenAppointmentModal = ({
    requestData,
    tabToOpen = tab.MAIN_INFO,
  }) => {
    // if it's the Clients page and the Pet tab then hide Pet Appointment Requests modal
    if (petsAppointmentRequestsNumber && isPetAppointmentRequestsModalOpen) {
      toggleAppointmentRequestsModalClass({
        action: 'add',
      });
    }

    openAppointmentRequestModal(tabToOpen);
    handleSetAppointmentRequestData(requestData);
    if (tabToOpen === tab.MAIN_INFO) handleSetDoctors(doctors);
  };

const handleCopyHistory = async (event, row) => {
  event.preventDefault();
  event.stopPropagation();

  if (copyingRequestId) {
    return;
  }

  setCopyBlockedRequestId(null);
  setCopyingRequestId(row.id);

  try {
    const response = await handleRequest(
      'get',
      `appointment-requests/${row.id}/messages`,
      true,
    );
    const messages = response.data || [];
    const { otherComments } = parseAvailabilityInfo(
      row.availabilityInfo || '',
    );

    const historyMessages = messages.map((m) => {
      if (!m._id) {
        const parts = [
          otherComments?.trim(),
          row.condition?.trim(),
        ].filter(Boolean);
        return {
          ...m,
          message: parts.length ? parts.join('\n') : 'No comments provided',
          authorType: 'client',
        };
      }
      return m;
    });

    const text = buildMessageHistoryTranscript({
      requestType: 'Appointment Request',
      header: {
        parent: row.parentFullName,
        pet: row.petName,
        doctor: row.doctorFullName,
        appointmentType: row.typeTitle || row.type,
        submitted: row.dateTime || `${row.date || ''} ${row.time || ''}`.trim(),
      },
      messages: historyMessages,
    });

    try {
      await navigator.clipboard.writeText(text);
    } catch (clipboardError) {
      console.error(clipboardError);
      setCopyBlockedRequestId(row.id);
      setCopyingRequestId(null);
      return;
    }

    if (copyBadgeTimeoutRef.current) {
      clearTimeout(copyBadgeTimeoutRef.current);
    }

    setCopiedRequestId(row.id);
    setCopyingRequestId(null);

    copyBadgeTimeoutRef.current = setTimeout(() => {
      setCopiedRequestId(null);
      copyBadgeTimeoutRef.current = null;
    }, COPIED_BADGE_DURATION_MS);
  } catch (error) {
    console.error(error);
    setCopyingRequestId(null);
  }
};

  const hasParent = heads.some(
    (head) => head && head.key === 'parentFullName',
  );
  const tableClassName = classNames(
    'custom-table',
    'loyal-table',
    'fixed-layout',
    {
      'appointment-request-table--no-parent': !hasParent,
    },
  );

  const renderRowValues = (row) => {
    const attachmentsColumn = (head) => {
      const isButtonVisible = !!row.documents?.length || !!row.photos?.length;

      return (
        <td
          key={head.key}
          className={classNames('custom-no-over-flow-td', head.className)}
          onClick={(e) => {
            isButtonVisible && e.stopPropagation();
          }}
          style={{ textAlign: 'center' }}
        >
          {isButtonVisible && (
            <a
              style={{ padding: 0 }}
              className="view-tab-button full-width"
              onClick={() =>
                handleOpenAppointmentModal({
                  requestData: row,
                  tabToOpen: tab.ATTACHMENTS,
                })
              }
            >
              View
            </a>
          )}
        </td>
      );
    };

    const preferredDatesColumn = (head, value) => {
      const formattedDates = value
        .map((dateRange) => formatPreferredDateRange(dateRange, 'string'))
        .join('\n');

      return (
        <td
          key={head.key}
          title={formattedDates}
          className={classNames('custom-no-over-flow-td', head.className)}
        >
          {formattedDates}
        </td>
      );
    };

    const renderMessageColumn = (head) => (
      <td
        key={head.key}
        className={classNames('custom-no-over-flow-td', head.className)}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="nav-link-style message-button"
          onClick={() => {
            handleSetAppointmentRequestData(row);
            setIsMessageModalOpen(true);
          }}
        >
          Message
        </button>
      </td>
    );

    const renderMessageStatusColumn = (head, value) => {
      const statusMap = APPOINTMENT_REQUEST_MESSAGE_STATUS;
      let dotClass = 'none';
      let columnText = '\u2013';

      switch (value) {
        case statusMap.NEW_REQUEST:
          dotClass = 'green';
          columnText = value;
          break;
        case statusMap.AWAIT_REPLY:
          dotClass = 'yellow';
          columnText = value;
          break;
        case statusMap.NEW_REPLY:
          dotClass = 'blue';
          columnText = value;
          break;
        case statusMap.STAFF_REPLIED:
          dotClass = 'purple';
          columnText = value;
          break;
        default:
          dotClass = 'none';
          columnText = value || '\u2013';
          break;
      }

      const hasAppointment = !!row.appointmentInfo?.id;

      return (
        <td key={head.key} className={head.className ?? ''}>
          <div className="message-status text-capitalize">
            {hasAppointment && (
              <CalendarMonthIcon
                width={16}
                height={16}
                className="appointment-request-reply-calendar-icon"
                aria-hidden
              />
            )}
            <span className={classNames('dot', dotClass)} />
            <span>{columnText}</span>
          </div>
        </td>
      );
    };

const renderCopyHistoryColumn = (head) => {
  const isCopying = copyingRequestId === row.id;
  const showCopiedBadge = copiedRequestId === row.id;
  const showBlockedPrompt = copyBlockedRequestId === row.id;

  return (
    <td
      key={head.key}
      className={classNames('custom-no-over-flow-td', head.className)}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="comm-copy-history-wrap">
        <button
          type="button"
          className="comm-copy-history-btn"
          title="Copy history"
          disabled={isCopying}
          onClick={(event) => handleCopyHistory(event, row)}
        >
          <ContentCopyIcon size={16} aria-hidden />
        </button>
      </span>
      {showBlockedPrompt ? (
        <CommCopyHistoryBlockedPrompt recordLabel="Appointment Request" />
      ) : null}
      {showCopiedBadge ? (
        <span className="comm-copy-history-copied-badge">Copied!</span>
      ) : null}
    </td>
  );
};

    const defaultColumn = (head, value, capitalizeClass) => {
      const sortKey = getSortFieldName(head.key);
      const isSortedField = sortField === sortKey;

      const tdClassName = classNames(
        'custom-no-over-flow-td',
        capitalizeClass,
        head.className,
        { 'sorted-column': isSortedField },
      );

      return (
        <td key={head.key} title={value} className={tdClassName}>
          {value}
        </td>
      );
    };

    return heads.map((head) => {
      const value = row[head.key];
      const capitalizeClass = head.capitalize ? 'text-capitalize' : '';

      if (head.key === 'copyHistory') {
        return renderCopyHistoryColumn(head);
      }

      if (head.key === 'attachments') {
        return attachmentsColumn(head);
      }

      if (head.key === 'message') {
        return renderMessageColumn(head);
      }

      if (head.key === 'messageStatus') {
        return renderMessageStatusColumn(head, value);
      }

      if (head.key === 'lastMsg') {
        return defaultColumn(head, value || '\u2013', capitalizeClass);
      }

      if (head.key === 'preferredDate') {
        return preferredDatesColumn(head, value);
      }

      return defaultColumn(head, value, capitalizeClass);
    });
  };

  const renderTableHeaders = () => {
    const renderSortableHeader = (head) => {
      const sortKey = getSortFieldName(head.key);
      const defaultOrder = getDefaultSortOrder(sortKey);
      const isSortedField = sortField === sortKey;

      const thClassName = classNames(head.className, {
        'sorted-column': isSortedField,
      });

      return (
        <th key={head.key} className={thClassName}>
          {sortField === sortKey ? (
            <SortingField
              icon={sortOrder === 1 ? 'triangle-up' : 'triangle-down'}
              name={head.name}
              onSort={() => handleSort({ order: sortOrder === 1 ? -1 : 1 })}
            />
          ) : (
            <SortingField
              name={head.name}
              icon="diamond-narrow"
              onSort={() => handleSort({ field: sortKey, order: defaultOrder })}
            />
          )}
        </th>
      );
    };

    const renderFilterableHeader = (head) => {
      const filterField = getFilterField(head.key);
      const filtersByKey = filters[filterField] ?? [];

      return (
        <th key={head.key} className={head.className ?? ''}>
          <TableFilterComponent
            allFilters={head.filterValue}
            filters={filtersByKey}
            action={(list) => {
              handleStatusFilter(head.key, list);
            }}
            label={head.name}
          />
        </th>
      );
    };

    const renderIconHeader = (head) => (
      <th key={head.key} className={head.className ?? ''}>
        <img src={head.imgSrc} alt={head.imgAlt} title={head.name} />
      </th>
    );

    const renderDefaultHeader = (head) => (
      <th key={head.key} className={head.className ?? ''}>
        <TableHeaderLabel
          id={`table-header-info-${head.key}`}
          label={head.name}
          tooltip={head.headerTooltip}
        />
      </th>
    );

    const renderHeader = (head) => {
      if (!head) return null;

      if (head.sortable) {
        return renderSortableHeader(head);
      } else if (head.filterable) {
        if (head.key === 'doctorFullName') {
          if (doctors.length === 0) return renderDefaultHeader(head);

          const filterValues = doctors.map(({ _id, name }) => ({
            [_id]: `Dr. ${name}`,
          }));

          filterValues.push({ NA: 'N/A' });

          head.filterValue = filterValues;
        }

        return renderFilterableHeader(head);
      } else if (head.imgSrc) {
        return renderIconHeader(head);
      } else {
        return renderDefaultHeader(head);
      }
    };

    return (
      <tr className="header-tr" style={stickyTop ? { top: stickyTop } : {}}>
        {heads.map((head) => renderHeader(head))}
      </tr>
    );
  };

  return (
    <div className="row table-row">
      <div className="col-md-12 table-wrapper">
        <Table striped className={tableClassName}>
          <thead>{renderTableHeaders()}</thead>
          <tbody>
            {rows.map((row, index) => {
              return (
                <tr
                  className="body-tr appointment-request-row"
                  key={index}
                  onClick={() =>
                    handleOpenAppointmentModal({ requestData: row })
                  }
                >
                  {renderRowValues(row)}
                </tr>
              );
            })}
          </tbody>
        </Table>

        {rows.length === 0 ? (
          <div className="no-data-message">{emptyStateMessage}</div>
        ) : null}
      </div>
    </div>
  );
}
